import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';

export enum BulletType {
  PLAYER = 'player',
  ENEMY = 'enemy'
}

export class Bullet implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private type: BulletType;
  private speed: number;
  private color: string;

  constructor(
    id: string,
    x: number,
    y: number,
    type: BulletType,
    renderManager: RenderManager,
    speedOverride?: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.size = { x: 2, y: 6 };
    this.renderManager = renderManager;
    this.type = type;
    
    // Set properties based on bullet type
    if (type === BulletType.PLAYER) {
      this.speed = speedOverride || 400; // Fast player bullets
      this.velocity = { x: 0, y: -this.speed };
      this.color = '#FFFFFF'; // White bullets for player
    } else {
      this.speed = speedOverride || 150; // Base enemy bullet speed, can be overridden
      this.velocity = { x: 0, y: this.speed };
      this.color = '#FFFF00'; // Bright yellow bullets for enemies (more visible)
    }
  }

  public update(deltaTime: number): void {
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Deactivate if off screen
    if (this.position.y < -10 || this.position.y > 610) {
      this.active = false;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    if (this.type === BulletType.PLAYER) {
      // Player bullets are thin vertical lines
      this.renderManager.fillRect(
        this.position.x, 
        this.position.y, 
        this.size.x, 
        this.size.y, 
        this.color
      );
    } else {
      // Enemy bullets are lightning bolt style
      this.drawEnemyBullet();
    }
  }

  private drawEnemyBullet(): void {
    const x = this.position.x;
    const y = this.position.y;
    
    // Draw zigzag pattern for enemy bullets
    const pattern = [
      [0,1],
      [1,0],
      [0,1],
      [1,0],
      [0,1],
      [1,0]
    ];
    
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          this.renderManager.fillRect(
            x + col,
            y + row,
            1,
            1,
            this.color
          );
        }
      }
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public getType(): BulletType {
    return this.type;
  }

  public destroy(): void {
    this.active = false;
  }
}